import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FloatingPopupLineChartWrapperComponent } from '@app/main/main-content/floating-popup-manager/components/floating-popup-line-chart-wrapper/floating-popup-line-chart-wrapper.component';
import { FloatingPopupContentChartActionsComponent } from '@app/main/main-content/floating-popup-manager/components/floating-popup-content-chart-actions/floating-popup-content-chart-actions.component';
import { FloatingPopupContentExportCsvActionsComponent } from '@app/main/main-content/floating-popup-manager/components/floating-popup-content-export-csv-actions/floating-popup-content-export-csv-actions.component';
import { FpOpenCustomCorrelationActionComponent } from '@app/main/leak-detection/components/fp-open-custom-correlation-action/fp-open-custom-correlation-action.component';
import { IFloatingPopupInstance } from '@app/main/main-content/floating-popup-manager/interfaces/floating-popup-instance';
import { FloatingPopupService } from '@app/main/main-content/floating-popup-manager/services/floating-popup.service';
import { FpLoggerNoiseSpectrumStore } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-spectrum/services/fp-logger-noise-spectrum/fp-logger-noise-spectrum.store';
import { FpLoggerNoiseSpectrumChartService } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-spectrum/services/fp-logger-noise-spectrum-chart.service';
import { FpLoggerNoiseSpectrumExportService } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-spectrum/services/fp-logger-noise-spectrum-export.service';
import { FpLoggerNoiseSpectrumActionType } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-spectrum/services/fp-logger-noise-spectrum/fp-logger-noise-spectrum.actions';
import { FpLoggerNoiseSoundSignalsActionComponent } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-spectrum/components/fp-logger-noise-sound-signals-action/fp-logger-noise-sound-signals-action.component';
import { FloatingPopupBaseErrorMessageComponent } from '@app/main/main-content/floating-popup-manager/components/floating-popup-base-error-message/floating-popup-base-error-message.component';
import { ElementWidthClassDirective } from '@app/shared/directives/element-width-class-directive';

@Component({
  selector: 'gc-fp-logger-noise-spectrum',
  standalone: true,
  imports: [
    ElementWidthClassDirective,
    FloatingPopupLineChartWrapperComponent,
    FloatingPopupContentChartActionsComponent,
    FloatingPopupContentExportCsvActionsComponent,
    FpOpenCustomCorrelationActionComponent,
    FpLoggerNoiseSoundSignalsActionComponent,
    FloatingPopupBaseErrorMessageComponent,
  ],
  templateUrl: './fp-logger-noise-spectrum.component.html',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'min-h-0 grow-1',
  },
})
export class FpLoggerNoiseSpectrumComponent {
  public readonly currentTabStore = inject(FpLoggerNoiseSpectrumStore);
  protected readonly chartService = inject(FpLoggerNoiseSpectrumChartService);
  protected readonly exportService = inject(FpLoggerNoiseSpectrumExportService);
  protected readonly floatingPopupService = inject(FloatingPopupService);

  public popup = input<IFloatingPopupInstance>();

  public isChartFullscreen = toSignal(this.currentTabStore.isChartFullscreenActive$, {
    initialValue: false,
  });

  private readonly popupMainData = toSignal(this.currentTabStore.popupMainData$, {
    initialValue: null,
  });

  private readonly data = toSignal(this.currentTabStore.data$, {
    initialValue: null,
  });

  public error = toSignal(this.currentTabStore.error$, {
    initialValue: null,
  });

  public isChartDataExist = computed(() => !!this.data()?.length);

  public readonly loggerDeployment = computed(() => this.popupMainData()?.deployment);
  public readonly soundSignalId = computed(() => this.popupMainData()?.measurement?.soundSignalId ?? null);
  public readonly soundSignalUrl = computed(() => this.popupMainData()?.measurement?.soundSignalUrl ?? null);
  public readonly soundSignalStartAt = computed(() => this.popupMainData()?.measurement?.soundSignalStartAt);

  public combinedChartData = computed(() => {
    const data = this.data() || [];
    const chartData = this.chartService.getDataForChart(data);
    const chartOptions = this.chartService.generateOptions(data);
    return { chartData, chartOptions };
  });

  public doExport() {
    const popupMainData = this.popupMainData();
    if (!popupMainData) return;
    this.exportService.exportCsv(popupMainData, this.data() || []);
  }

  public toggleChartFullscreen() {
    this.currentTabStore.dispatch({
      type: FpLoggerNoiseSpectrumActionType.ToggleChartFullscreen,
      synchronizeWithPopupId: this.popup()?.id || undefined,
    });
  }
}
